import { useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { useAlertContext } from '@/contexts/AlertContext'
import OfferedBidPtusDataGrid from '@/features/bidding/components/capacity/offer/OfferedBidPtusDataGrid'
import { useBidOfferDataContext } from '@/features/bidding/contexts/BidOfferDataContext'
import { useResetPtusContext } from '@/features/bidding/contexts/ResetPtusContext'
import { useMarketClosingWarning } from '@/features/bidding/hooks/useMarketClosingWarning'
import { usePtuExpiration } from '@/features/bidding/hooks/usePtuExpiration'
import { useSaveBidsMutation } from '@/features/bidding/hooks/useSaveBidsMutation'
import type {
  BidMetaData,
  BulkFillMode,
  OfferedAndForecastAndSuggestedPtu,
  PtuChunk,
} from '@/features/bidding/types/bid'
import { bidFromOfferedAndForecastPtus } from '@/features/bidding/utils/model/createBid'
import { findExpiredPtuPrefillSource } from '@/features/bidding/utils/ptuPrefillSources'

type OfferedBidFormProps = {
  bidMetaData: BidMetaData
}

/**
 * Whether two PTU chunk ladders carry the same (offeredPrice, offeredVolume.quantity) pairs.
 * Undefined and empty arrays are treated as equal, since both mean "no ladder".
 */
function chunksMatch(a: PtuChunk[] | undefined, b: PtuChunk[] | undefined): boolean {
  const chunksA = a ?? []
  const chunksB = b ?? []

  if (chunksA.length !== chunksB.length) return false

  return chunksA.every(
    (chunk, index) =>
      chunk.offeredPrice === chunksB[index]?.offeredPrice &&
      chunk.offeredVolume.quantity === chunksB[index]?.offeredVolume.quantity,
  )
}

const OfferedBidForm = ({ bidMetaData }: Readonly<OfferedBidFormProps>) => {
  const { t } = useTranslation()
  const { pushAlert } = useAlertContext()
  const { markPtuAsReset } = useResetPtusContext()
  const { showMarketClosingWarning, clearMarketClosingWarning } = useMarketClosingWarning()
  const { isPtuEditable, currentTime } = usePtuExpiration()
  const { saveBids } = useSaveBidsMutation()
  const isSavingBids = useRef(false)

  // Get data and centralized actions from context
  const {
    latestNonDraftBidsByActivationGroup,
    latestAcceptedBidsByActivationGroup,
    currentPtusByActivationGroup,
    updatePtusForActivationGroup,
    // Centralized actions
    saveDraftForActivationGroup,
    processBidPtuUpdate: processBidPtuUpdateAction,
    fillAllPtusForActivationGroup,
    handleClipboardPaste,
    getDataGridValues,
  } = useBidOfferDataContext()

  const latestNonDraftBid = latestNonDraftBidsByActivationGroup.get(bidMetaData.activationGroupUuid)
  const latestAcceptedBid = latestAcceptedBidsByActivationGroup.get(bidMetaData.activationGroupUuid)
  const bidPtus = currentPtusByActivationGroup.get(bidMetaData.activationGroupUuid) ?? []

  // Get computed values for the DataGrid
  const { acceptsOnlyPositiveInteger, ptusForTheDataGridRows, volumeUnit } = getDataGridValues(
    bidMetaData.activationGroupUuid,
  )

  // Wrap centralized actions with the current activationGroupUuid
  const saveDraftCurrentValues = useCallback(
    () => saveDraftForActivationGroup(bidMetaData.activationGroupUuid),
    [saveDraftForActivationGroup, bidMetaData.activationGroupUuid],
  )

  const processBidPtuUpdate = useCallback(
    (newPtu: OfferedAndForecastAndSuggestedPtu) => processBidPtuUpdateAction(bidMetaData.activationGroupUuid, newPtu),
    [processBidPtuUpdateAction, bidMetaData.activationGroupUuid],
  )

  const fillAllPtus = useCallback(
    (bulkMode: BulkFillMode, bulkFillValue: number | undefined) =>
      fillAllPtusForActivationGroup(bidMetaData.activationGroupUuid, bulkMode, bulkFillValue),
    [fillAllPtusForActivationGroup, bidMetaData.activationGroupUuid],
  )

  const deliveryISODate = bidMetaData.deliveryDay.toISODate()

  // Show market closing warning on mount and cleanup on unmount
  useEffect(() => {
    showMarketClosingWarning(bidMetaData.deliveryDay, bidMetaData.marketProgram, bidMetaData.bidType)

    return () => {
      clearMarketClosingWarning()
    }
  }, [
    deliveryISODate,
    bidMetaData.marketProgram,
    bidMetaData.bidType,
    showMarketClosingWarning,
    clearMarketClosingWarning,
  ])

  // Handle paste from clipboard
  useEffect(() => {
    const handlePaste = (event: ClipboardEvent) => {
      const clipboardText = event.clipboardData?.getData('text') ?? ''
      if (clipboardText) {
        handleClipboardPaste(bidMetaData.activationGroupUuid, clipboardText)
      }
    }

    document.addEventListener('paste', handlePaste)
    return () => {
      document.removeEventListener('paste', handlePaste)
    }
  }, [handleClipboardPaste, bidMetaData.activationGroupUuid])

  // Reset non-editable PTUs when a PTU crosses its gate closure while the page is open.
  // The value comes from the accepted version, exactly as the initial prefill does (BID-209), and
  // the price chunks travel with it so the PTU total never disagrees with its own ladder (BID-252).
  useEffect(() => {
    // Don't process if we're already saving to prevent duplicate calls
    if (isSavingBids.current) return

    let hasChanged = false

    const newBidPtus = bidPtus.map((ptu) => {
      const editable = isPtuEditable(ptu.ptu.start, bidMetaData.bidType, bidMetaData.marketProgram)

      // Editable PTUs keep their current values
      if (editable) {
        return ptu
      }

      // For non-editable PTUs, use the accepted version and fall back to the latest sent one. The
      // submitted quantity must equal the source PTU's offeredVolume, since that is the field
      // Portfolio Manager diffs against its baseline, at both PTU and chunk level.
      const expiredPtuSource = findExpiredPtuPrefillSource(ptu.ptu.start, latestAcceptedBid, latestNonDraftBid)
      const expectedQuantity = expiredPtuSource?.offeredVolume.quantity ?? 0
      const expectedPtuChunks = expiredPtuSource?.ptuChunks

      // The PTU is unchanged only when both the quantity and its chunk ladder already match - the
      // chunks travel with the volume (BID-252), so a stale ladder alone still needs a reset.
      if (ptu.offeredVolume.quantity === expectedQuantity && chunksMatch(ptu.offeredPtuChunks, expectedPtuChunks)) {
        return ptu
      }

      hasChanged = true

      // Try to mark this PTU as reset, only succeeds if PTU expired during this session
      const wasMarkedAsReset = markPtuAsReset({
        activationGroupUuid: bidMetaData.activationGroupUuid,
        ptuStart: ptu.ptu.start,
        bidType: bidMetaData.bidType,
        marketProgram: bidMetaData.marketProgram,
      })
      // Only show alert if the PTU was actually marked
      if (wasMarkedAsReset) {
        pushAlert({
          severity: 'warning',
          message: t('bidding.create_bid.ptu_reset_alert.message', {
            ptu: ptu.ptu.start.toFormat('HH:mm'),
            activationGroup: bidMetaData.activationGroupName,
          }),
          title: t('bidding.create_bid.ptu_reset_alert.title', { ptu: ptu.ptu.start.toFormat('HH:mm') }),
        })
      }

      return {
        ...ptu,
        offeredVolume: { ...ptu.offeredVolume, quantity: expectedQuantity },
        offeredPtuChunks: expectedPtuChunks,
      }
    })

    if (hasChanged) {
      updatePtusForActivationGroup(bidMetaData.activationGroupUuid, newBidPtus)
      // Set flag to prevent concurrent saves
      isSavingBids.current = true
      saveBids([bidFromOfferedAndForecastPtus(bidMetaData, newBidPtus)]).finally(() => {
        isSavingBids.current = false
      })
    }
  }, [
    currentTime.toMillis(),
    bidPtus,
    latestNonDraftBid,
    latestAcceptedBid,
    isPtuEditable,
    bidMetaData.bidType,
    bidMetaData.marketProgram,
    bidMetaData.activationGroupUuid,
    markPtuAsReset,
  ])

  return (
    <OfferedBidPtusDataGrid
      acceptsOnlyPositiveInteger={acceptsOnlyPositiveInteger}
      bidMetaData={bidMetaData}
      processRowUpdate={processBidPtuUpdate}
      ptus={ptusForTheDataGridRows}
      saveDraftCurrentValues={saveDraftCurrentValues}
      volumeUnit={volumeUnit}
      onFillAll={fillAllPtus}
    />
  )
}

export default OfferedBidForm
